import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Video, Loader2, Play, Download, Trash2, Clock, CheckCircle, XCircle, Sparkles, X, Upload, Film, Link as LinkIcon, Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIVideoBuilder() {
  const { user, token, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [myVideos, setMyVideos] = useState([]);
  const [pollingJobs, setPollingJobs] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [generationProgress, setGenerationProgress] = useState({});
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [credits, setCredits] = useState(null);
  const [creditCosts, setCreditCosts] = useState({});
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    business_name: '',
    offer_description: '',
    tone: 'professional',
    target_audience: '',
    duration: 5,
    aspect_ratio: '16:9'
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!['growth', 'founder'].includes(user.membership_tier) && !user.is_admin) {
      toast.error('AI Video Builder is only available for Growth and Founder members');
      navigate('/dashboard');
      return;
    }
    fetchMyVideos();
    fetchCredits();
  }, [user, authLoading]);

  const fetchCredits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-credits`, { headers: getAuthHeader() });
      setCredits(res.data.credits);
      setCreditCosts(res.data.credit_costs);
    } catch (e) {
      console.error('Failed to fetch credits');
    }
  };

  // Poll for pending videos with progress simulation
  useEffect(() => {
    if (pollingJobs.length === 0) return;
    
    const interval = setInterval(async () => {
      for (const jobId of pollingJobs) {
        // Simulate progress
        setGenerationProgress(prev => {
          const currentProgress = prev[jobId] || 0;
          const newProgress = Math.min(currentProgress + Math.random() * 15, 95);
          return { ...prev, [jobId]: newProgress };
        });

        try {
          const response = await axios.get(
            `${API_URL}/api/ai-videos/status/${jobId}`,
            { headers: getAuthHeader() }
          );
          
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            setPollingJobs(prev => prev.filter(id => id !== jobId));
            setGenerationProgress(prev => {
              const updated = { ...prev };
              if (response.data.status === 'completed') {
                updated[jobId] = 100;
              }
              return updated;
            });
            fetchMyVideos();
            
            if (response.data.status === 'completed') {
              toast.success('Video generation completed!');
            } else {
              toast.error('Video generation failed. The AI service may be experiencing issues — please try again later.');
            }
          }
        } catch (error) {
          console.error('Error polling video status:', error);
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pollingJobs]);

  const fetchMyVideos = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-videos/my-videos`,
        { headers: getAuthHeader() }
      );
      setMyVideos(response.data);
      
      // Add pending jobs to polling
      const pendingJobs = response.data
        .filter(v => v.status === 'pending' || v.status === 'processing')
        .map(v => v.id);
      
      // Initialize progress for pending jobs
      const initialProgress = {};
      pendingJobs.forEach(id => {
        initialProgress[id] = generationProgress[id] || Math.random() * 30;
      });
      setGenerationProgress(prev => ({ ...prev, ...initialProgress }));
      setPollingJobs(pendingJobs);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.offer_description) {
      toast.error('Please fill in business name and offer description');
      return;
    }

    const creditsNeeded = creditCosts[formData.duration] || 1;
    if (credits !== null && credits < creditsNeeded) {
      toast.error(`Not enough credits. Need ${creditsNeeded}, have ${credits}. Purchase more credits below.`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-videos/generate`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success(`Video generation started! Credits used: ${response.data.credits_used}`);
      if (response.data.credits_remaining !== undefined) {
        setCredits(response.data.credits_remaining);
      }
      const jobId = response.data.job_id;
      setPollingJobs(prev => [...prev, jobId]);
      setGenerationProgress(prev => ({ ...prev, [jobId]: 5 }));
      fetchMyVideos();
      
      setFormData({
        ...formData,
        business_name: '',
        offer_description: '',
        target_audience: ''
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && (detail.includes('tier') || detail.includes('credits') || detail.includes('Credit'))) {
        toast.error(detail);
      } else {
        toast.error('The AI video service is temporarily unavailable. Please try again in a few minutes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async (packSize) => {
    setPurchasingCredits(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-credits/purchase`, {
        pack_size: packSize,
        origin_url: window.location.origin
      }, { headers: getAuthHeader() });
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase credits');
    } finally {
      setPurchasingCredits(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/ai-videos/${jobId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Video deleted');
      if (selectedVideo?.id === jobId) {
        setSelectedVideo(null);
      }
      fetchMyVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video');
    }
  };

  const playVideo = async (video) => {
    setSelectedVideo(video);
    setLoadingVideo(false);
    // Use the public URL directly — native <video> handles HTTP 206 streaming.
    const publicUrl = `${API_URL}/api/ai-videos/${video.id}/public`;
    setVideoBlobUrl(publicUrl);
  };

  const closeVideoModal = () => {
    setVideoBlobUrl(null);
    setSelectedVideo(null);
  };

  // Generate shareable URL for a video
  const getShareableUrl = (videoId) => {
    return `${API_URL}/api/ai-videos/${videoId}/public`;
  };

  const copyVideoUrl = async (video) => {
    const url = getShareableUrl(video.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(video.id);
      toast.success('Video URL copied to clipboard!');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const downloadVideo = async (video) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ai-videos/${video.id}/stream`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${video.business_name}_promo.mp4`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download video');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Ready to play';
      case 'failed':
        return 'Generation failed';
      case 'processing':
        return 'Generating...';
      default:
        return 'Queued';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user || (!['growth', 'founder'].includes(user.membership_tier) && !user.is_admin)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/dashboard">
              <Button variant="ghost" className="mb-4 text-foreground hover:text-primary" data-testid="back-btn">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-playfair font-bold text-foreground flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary" />
              AI Video Builder
            </h1>
            <p className="text-muted-foreground mt-2">
              Create professional promotional videos for your business using AI
            </p>
          </div>
          <Link to="/video-library">
            <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
              <Film className="w-4 h-4 mr-2" /> Video Library
            </Button>
          </Link>
        </div>

        {/* Video Player Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b border-border">
                <h3 className="font-semibold text-foreground">{selectedVideo.business_name}</h3>
                <Button variant="ghost" size="sm" onClick={closeVideoModal}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="aspect-video bg-black relative">
                {loadingVideo ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin" />
                    <span className="ml-3 text-white">Loading video...</span>
                  </div>
                ) : videoBlobUrl ? (
                  <video
                    key={videoBlobUrl}
                    src={videoBlobUrl}
                    controls
                    autoPlay
                    playsInline
                    preload="auto"
                    className="w-full h-full bg-black"
                    onError={(e) => {
                      console.error('Video playback error:', e);
                      toast.error('Playback error — try "Watch in New Tab" or download the file.');
                    }}
                    data-testid="ai-video-player"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    Failed to load video
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-muted-foreground">
                    {selectedVideo.duration}s • {selectedVideo.aspect_ratio || '16:9'} • Seedance 2.0
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => window.open(getShareableUrl(selectedVideo.id), '_blank')}
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                    >
                      <Play className="w-4 h-4 mr-2" /> Watch in New Tab
                    </Button>
                    <Button onClick={() => downloadVideo(selectedVideo)} className="bg-primary text-primary-foreground">
                      <Download className="w-4 h-4 mr-2" /> Download
                    </Button>
                  </div>
                </div>
                {/* Shareable URL */}
                <div className="bg-background/50 rounded-lg p-3 border border-border">
                  <Label className="text-xs text-muted-foreground mb-1 block">Shareable URL (use in lessons, pages, etc.)</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={getShareableUrl(selectedVideo.id)} 
                      readOnly 
                      className="bg-input text-foreground text-sm flex-1"
                    />
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => copyVideoUrl(selectedVideo)}
                      className="border-primary text-primary hover:bg-primary/10"
                    >
                      {copiedUrl === selectedVideo.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Video Generation Form */}
          <div className="wm-card p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Create New Video
            </h2>
            <p className="text-xs text-muted-foreground mb-4">Powered by Seedance 2.0</p>

            {/* Credits Display */}
            <div className="bg-gradient-to-r from-primary/10 to-emerald-500/10 rounded-lg p-4 mb-6 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">AI Credits</p>
                  <p className="text-2xl font-bold text-foreground" data-testid="credit-balance">{credits ?? '...'}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('credit-packs')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border-primary text-primary hover:bg-primary/10"
                  data-testid="buy-credits-btn"
                >
                  Buy Credits
                </Button>
              </div>
              {credits === 1 && <p className="text-xs text-emerald-400 mt-1">You have 1 free credit to try!</p>}
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="video-form">
              <div>
                <Label htmlFor="business_name" className="text-foreground">Business Name *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="e.g., Acme Marketing Solutions"
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="business-name-input"
                />
              </div>

              <div>
                <Label htmlFor="offer_description" className="text-foreground">Offer Description *</Label>
                <Textarea
                  id="offer_description"
                  value={formData.offer_description}
                  onChange={(e) => setFormData({ ...formData, offer_description: e.target.value })}
                  placeholder="Describe your product/service and what makes it special..."
                  rows={3}
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="offer-input"
                />
              </div>

              <div>
                <Label htmlFor="target_audience" className="text-foreground">Target Audience</Label>
                <Input
                  id="target_audience"
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  placeholder="e.g., Small business owners, entrepreneurs"
                  className="mt-1 bg-input border-border text-foreground"
                  data-testid="audience-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tone" className="text-foreground">Video Tone</Label>
                  <select
                    id="tone"
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                    className="mt-1 w-full bg-input border border-border text-foreground rounded-md px-3 py-2"
                    data-testid="tone-select"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="exciting">Exciting</option>
                    <option value="inspirational">Inspirational</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="duration" className="text-foreground">Duration</Label>
                  <select
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className="mt-1 w-full bg-input border border-border text-foreground rounded-md px-3 py-2"
                    data-testid="duration-select"
                  >
                    <option value={5}>5 seconds (1 credit)</option>
                    <option value={10}>10 seconds (2 credits)</option>
                    <option value={15}>15 seconds (3 credits)</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="aspect_ratio" className="text-foreground">Aspect Ratio</Label>
                <select
                  id="aspect_ratio"
                  value={formData.aspect_ratio}
                  onChange={(e) => setFormData({ ...formData, aspect_ratio: e.target.value })}
                  className="mt-1 w-full bg-input border border-border text-foreground rounded-md px-3 py-2"
                  data-testid="aspect-ratio-select"
                >
                  <option value="16:9">Landscape (16:9)</option>
                  <option value="9:16">Portrait (9:16)</option>
                  <option value="1:1">Square (1:1)</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={loading || (credits !== null && credits < (creditCosts[formData.duration] || 1))}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Starting Generation...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" /> Generate Video ({creditCosts[formData.duration] || 1} credit{(creditCosts[formData.duration] || 1) > 1 ? 's' : ''})
                  </>
                )}
              </Button>

              {credits !== null && credits < (creditCosts[formData.duration] || 1) && (
                <p className="text-xs text-red-400 text-center">Not enough credits. Purchase more below.</p>
              )}

              <p className="text-xs text-muted-foreground text-center">
                Seedance 2.0 video generation typically takes 2-5 minutes
              </p>
            </form>

            {/* Credit Purchase Section */}
            <div id="credit-packs" className="mt-8 pt-6 border-t border-border">
              <h3 className="text-lg font-semibold text-foreground mb-2">Purchase AI Credits</h3>
              <p className="text-xs text-muted-foreground mb-4">Use credits for video generation, AI descriptions, and more.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { size: 5, price: '$12.99', label: '5 Credits', perCredit: '$2.60' },
                  { size: 10, price: '$22.99', label: '10 Credits', perCredit: '$2.30' },
                  { size: 25, price: '$49.99', label: '25 Credits', popular: true, perCredit: '$2.00' },
                  { size: 50, price: '$89.99', label: '50 Credits', perCredit: '$1.80' }
                ].map(pack => (
                  <button
                    key={pack.size}
                    onClick={() => handlePurchaseCredits(pack.size)}
                    disabled={purchasingCredits}
                    className={`p-3 rounded-lg border text-center transition hover:scale-105 ${
                      pack.popular 
                        ? 'border-primary bg-primary/10 hover:bg-primary/20' 
                        : 'border-border hover:bg-white/5'
                    }`}
                    data-testid={`credit-pack-${pack.size}`}
                  >
                    {pack.popular && <span className="text-[10px] text-primary font-bold uppercase">Best Value</span>}
                    <p className="text-lg font-bold text-foreground">{pack.label}</p>
                    <p className="text-sm text-primary font-semibold">{pack.price}</p>
                    {pack.perCredit && <p className="text-[10px] text-muted-foreground">{pack.perCredit}/credit</p>}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* My Videos List */}
          <div className="wm-card p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" /> My AI Videos ({myVideos.length})
            </h2>
            
            {myVideos.length === 0 ? (
              <div className="text-center py-12">
                <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No videos generated yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first promotional video!
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {myVideos.map((video) => (
                  <div
                    key={video.id}
                    className="border border-border rounded-lg p-4 hover:bg-white/5 transition"
                    data-testid="video-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(video.status)}
                          <h3 className="font-semibold text-foreground">{video.business_name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-1">
                          {video.duration}s • {video.aspect_ratio || '16:9'} • {video.tone || 'Seedance 2.0'}
                        </p>
                        <p className="text-xs text-muted-foreground mb-2">
                          {new Date(video.created_at).toLocaleDateString()} • {getStatusText(video.status)}
                        </p>
                        
                        {/* Progress Bar for Processing Videos */}
                        {(video.status === 'pending' || video.status === 'processing') && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                              <span>Generating video...</span>
                              <span>{Math.round(generationProgress[video.id] || 0)}%</span>
                            </div>
                            <Progress 
                              value={generationProgress[video.id] || 0} 
                              className="h-2 bg-slate-700"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Please wait, this may take a few minutes
                            </p>
                          </div>
                        )}
                        
                        {video.status === 'failed' && (
                          <div className="mt-2 flex items-start gap-2 text-xs bg-red-500/10 rounded-md p-2">
                            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-red-400 font-medium">Generation failed</p>
                              <p className="text-red-400/80 mt-0.5">
                                The AI video service encountered an issue. This is usually temporary — please try again in a few minutes.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        {video.status === 'completed' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => playVideo(video)}
                              className="border-green-500 text-green-400 hover:bg-green-500/10"
                              data-testid="play-btn"
                              title="Play video"
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyVideoUrl(video)}
                              className="border-primary text-primary hover:bg-primary/10"
                              data-testid="copy-url-btn"
                              title="Copy shareable URL"
                            >
                              {copiedUrl === video.id ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadVideo(video)}
                              className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                              data-testid="download-btn"
                              title="Download video"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(video.id)}
                          className="border-red-400 text-red-500 hover:bg-red-50 hover:text-red-600"
                          data-testid="delete-btn"
                          title="Delete video"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 wm-card p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">Tips for Great Videos</h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-muted-foreground">
            <div>
              <h4 className="font-medium text-foreground mb-2">Business Name</h4>
              <p>Keep it clear and memorable. This will be featured prominently in your video.</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Offer Description</h4>
              <p>Be specific about what you're offering and the value it provides to customers.</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Target Audience</h4>
              <p>Defining your audience helps create more relevant and engaging content.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
